import React from "react";

const CICDPipeline = () => {
  return (
    <section className="min-h-screen bg-slate-900 text-white px-6 py-12">
      <h1 className="text-4xl font-bold mb-6">
        CI/CD Pipeline – Parameterized Deployment
      </h1>

      <p className="mb-4 text-gray-300">
        In modern software engineering, deploying code is no longer a simple
        matter of manually copying files to a server. Instead, teams aim to
        achieve automation, repeatability, and zero-downtime rollouts using{" "}
        <strong>Continuous Integration</strong> and{" "}
        <strong>Continuous Deployment</strong> (CI/CD) pipelines. In this
        project, we implemented a <strong>parameterized CI/CD pipeline</strong>{" "}
        that allows developers to select the exact branch and commit they want
        to deploy—without editing Jenkinsfiles manually.
      </p>

      <p className="mb-4 text-gray-300">
        The idea is simple but powerful: instead of hardcoding which branch to
        build, we expose a parameter in Jenkins that lets the user pick one at
        runtime. Jenkins then runs a Groovy script that calls the GitHub API to
        fetch the most recent commits for that branch. The selected commit SHA
        is then used to check out the code, build a Docker image, push it to
        Docker Hub, and deploy it to a Kubernetes cluster hosted on Google Cloud
        Platform (GCP).
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">Key Features</h2>
      <ul className="list-disc ml-6 space-y-2 text-gray-300">
        <li>
          <strong>Choice parameter</strong> to select GitHub branch directly in
          Jenkins.
        </li>
        <li>
          Fetch latest 10 commits dynamically from{" "}
          <code>api.github.com</code> using a Groovy script.
        </li>
        <li>Retrieve the selected commit ID for targeted builds.</li>
        <li>
          Checkout the commit, build Docker image, and push to Docker Hub.
        </li>
        <li>
          Deploy containerized app to GCP Kubernetes cluster using{" "}
          <code>kubectl</code>.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6 mb-2">Pipeline Workflow</h2>
      <ol className="list-decimal ml-6 space-y-2 text-gray-300">
        <li>
          Select branch in Jenkins via Choice Parameter or Active Choices
          Parameter.
        </li>
        <li>
          Groovy script calls GitHub API to list commits for the selected
          branch.
        </li>
        <li>
          Pick commit → build Docker image → push to Docker Hub.
        </li>
        <li>
          Deploy image to GCP Kubernetes using <code>kubectl</code>.
        </li>
      </ol>

      <h2 className="text-2xl font-semibold mt-6 mb-2">Tech Stack</h2>
      <div className="flex gap-4 flex-wrap">
        <span className="px-3 py-1 bg-blue-700 rounded">Jenkins</span>
        <span className="px-3 py-1 bg-green-700 rounded">Groovy</span>
        <span className="px-3 py-1 bg-orange-600 rounded">Docker</span>
        <span className="px-3 py-1 bg-yellow-600 rounded">GCP Kubernetes</span>
        <span className="px-3 py-1 bg-purple-700 rounded">Terraform</span>
      </div>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        Why Parameterized Pipelines Matter
      </h2>
      <p className="mb-4 text-gray-300">
        Traditional pipelines often tie deployments to specific branches, like
        <code>main</code> or <code>develop</code>. While this works for simple
        projects, real-world scenarios require more flexibility. You may need
        to:
      </p>
      <ul className="list-disc ml-6 space-y-2 text-gray-300">
        <li>Deploy a hotfix from a feature branch directly to production.</li>
        <li>Test a proof-of-concept branch in a staging environment.</li>
        <li>Roll back to a specific commit after an unexpected bug.</li>
      </ul>
      <p className="mt-4 text-gray-300">
        By parameterizing the pipeline, we turn Jenkins into an interactive
        deployment tool where the operator can choose what goes live.
      </p>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        Groovy Example – Active Choices Reactive Parameter
      </h2>
      <pre className="bg-black text-green-400 p-4 rounded text-sm overflow-x-auto">
{`import groovy.json.JsonSlurper

def branchParam = binding.variables.get("BRANCH_NAME") ?: "origin/master"
def branch = branchParam.replace("origin/", "")

def gitUrl = "https://api.github.com/repos/username/repo/commits?sha=\\\${branch}&per_page=10"

def token = "**********"

def cmd = [
    "curl", "-s",
    "-H", "Authorization: token \\\${token}",
    gitUrl
]
def process = cmd.execute()
process.waitFor()

if (process.exitValue() != 0) {
    return ["Curl failed: \\\${process.err.text}"]
}

def json = new JsonSlurper().parseText(process.text)
return json.collect { it.sha.toString() }`}
      </pre>

      <h2 className="text-2xl font-semibold mt-6 mb-2">
        Step-by-Step Deployment Process
      </h2>
      <p className="mb-4 text-gray-300">
        Below is a detailed explanation of each step our pipeline performs:
      </p>
      <ol className="list-decimal ml-6 space-y-2 text-gray-300">
        <li>
          <strong>Parameter Input:</strong> Jenkins job starts by asking the
          user which branch and commit to deploy.
        </li>
        <li>
          <strong>Commit Retrieval:</strong> Groovy script queries GitHub API to
          fetch latest commits.
        </li>
        <li>
          <strong>Code Checkout:</strong> Jenkins pulls the selected commit from
          Git.
        </li>
        <li>
          <strong>Docker Build:</strong> The codebase is containerized.
        </li>
        <li>
          <strong>Push to Docker Hub:</strong> The built image is uploaded.
        </li>
        <li>
          <strong>Kubernetes Deploy:</strong> Manifest files are applied to the
          GCP Kubernetes cluster.
        </li>
      </ol>

      <h2 className="text-2xl font-semibold mt-6 mb-2">Final Thoughts</h2>
      <p className="text-gray-300">
        This parameterized approach to CI/CD greatly improves flexibility and
        control over deployments. It minimizes risks during production releases
        and gives teams the ability to respond quickly to changing requirements
        or urgent bug fixes.
      </p>
    </section>
  );
};

export default CICDPipeline;
